/*
 * NOTE: for ease, it is best to use the changeboarding CLI script to contribute
 * a new message to this file.
 *
 * yarn changeboarding add:experience <feature-name> <experiment> <enabled (optional)>
 */

import { getAbilitiesManager } from '@canvas/abilities/abilities-manager/singleton';
import {
  type ExperimentName,
  isExperimentName,
  getExperimentValue,
} from '@canvas/client-feature-flags';
import { PaywallFeature, isPaywallUnlocked } from '@canvas/paywall';

export const PLACEMENT_ID = 'ct-changeboarding-message';

import { getChangeboardingOverrideFeature } from './query.ts';

interface ChangeboardingMessageConfig {
  /**
   * A default value for whether the feature is enabled. If false and this
   * fallback default is being used to check whether the feature is enabled,
   * then the changeboarding will not be shown to the user.
   */
  enabled: boolean;
  /**
   * An override killswitch value for when we want to completely disable the
   * changeboarding experience no matter what.
   */
  killswitch: boolean;
  /**
   * A paywall feature to check if the feature is enabled. The changeboarding
   * will not be shown to the user if the user is not entitled to the feature.
   */
  paywallFeature?: PaywallFeature;
  /** Optional config to enable auto-dismissal after thresholds */
  autoDismissal?: {
    /** Dismiss after this many distinct views (exposures) */
    times?: number;
    /** Dismiss after this many distinct days viewed */
    days?: number;
  };
  /**
   * An experiment flag to determine if this changeboarding ui
   * can be shown to a user.
   */
  experiment?: ExperimentName;
}

export type FeatureName = keyof typeof messages;

export const messages = {
  cloudShapes: {
    enabled: false,
    killswitch: true,
    // TODO: Change to PaywallFeature.CLOUD_ARCHITECTURE_SHAPES when it is ready
    // Using this feature for now to wire up the paywall upgrade CTA
    paywallFeature: PaywallFeature.SMART_SECTIONS,
    autoDismissal: {
      times: 3,
      days: 3,
    },
  },
  dbGrouping: {
    experiment: 'databasesBoardGroupingOnboarding',
    enabled: false,
    killswitch: false,
    paywallFeature: undefined,
    autoDismissal: {
      times: 2,
      days: 3,
    },
  },
  dbAiSummary: {
    experiment: 'databasesFrontendAiFieldsSummary',
    enabled: false,
    killswitch: false,
    paywallFeature: undefined,
    autoDismissal: {
      times: undefined,
      days: undefined,
    },
  },
  dbContextualMenusM2FieldMenuTour: {
    experiment: 'databasesContextualMenusM2',
    enabled: false,
    killswitch: false,
    paywallFeature: undefined,
    autoDismissal: {
      times: undefined,
      days: undefined,
    },
  },
} as const;

/**
 * Helper function to check if a feature is enabled.
 * @param featureName - The name of the feature to check if it is enabled
 * @returns Whether the feature is enabled
 */
export const isFeatureEnabled = (featureName: FeatureName): boolean => {
  const config: ChangeboardingMessageConfig = messages[featureName];

  // Check if changeboarding is enabled in the current mode's capabilities
  const changeboardingCapability =
    getAbilitiesManager().capabilities?.interface?.changeboarding;
  if (changeboardingCapability?.enabled === false) {
    return false;
  }

  if (config.paywallFeature && !isPaywallUnlocked(config.paywallFeature)) {
    return false;
  }

  // If URL override is present for this feature, treat as enabled regardless of
  // killswitch, flags, or experiments. Valtio seen-state is still enforced in
  // the rendering layer.
  const overrideForFeature = getChangeboardingOverrideFeature();
  if (overrideForFeature === featureName) {
    return true;
  }

  if (config.killswitch) {
    return false;
  }

  if (config.experiment && isExperimentName(config.experiment)) {
    return getExperimentValue(config.experiment, 'enabled', {
      // We typically don't want to fire an exposure event for changeboarding
      // messages being shown for a particular experiment that unveils a feature.
      fireExperimentExposure: false,
    });
  }

  return config?.enabled ?? false;
};
